import { z } from 'zod';
import { MICE_PROFILE_FIELDS } from './mice_profile';

/**
 * O que aconteceu com um pedido, na ordem em que a compra anda, e as decisões do cadastro do
 * viajante (`profile_*`, spec B6): gravou no legado, manteve o da VOLL ou a gravação falhou.
 */
export const MICE_ORDER_EVENT_KINDS = [
  'queued',
  'unqueued',
  'purchasing',
  'reserved',
  'issued',
  'failed',
  'indeterminate',
  'released',
  'price_drift',
  'profile_written',
  'profile_kept',
  'profile_write_failed',
  'need_marked',
  'need_choice_changed',
  'need_asked_agent',
  'need_ordered_bulk',
  'email_corrected',
  'cost_center_changed',
] as const;
export const MiceOrderEventKindSchema = z.enum(MICE_ORDER_EVENT_KINDS);
/** As decisões do cadastro (spec B6): o histórico do painel do viajante só lê estas. */
export const MICE_PROFILE_EVENT_KINDS = [
  'profile_written',
  'profile_kept',
  'profile_write_failed',
] as const satisfies readonly (typeof MICE_ORDER_EVENT_KINDS)[number][];
/** O que o operador fez numa necessidade (sem pedido): marcar, trocar voo, pedir ao agente, emitir,
 *  corrigir o e-mail. */
export const MICE_NEED_EVENT_KINDS = [
  'need_marked',
  'need_choice_changed',
  'need_asked_agent',
  'need_ordered_bulk',
  'email_corrected',
  'cost_center_changed',
] as const satisfies readonly (typeof MICE_ORDER_EVENT_KINDS)[number][];
export type MiceNeedEventKind = (typeof MICE_NEED_EVENT_KINDS)[number];
export type MiceOrderEventKind = z.infer<typeof MiceOrderEventKindSchema>;

export const MiceOrderEventViewSchema = z.object({
  id: z.string(),
  /** `null` = evento do cadastro de um viajante de necessidade. */
  orderId: z.string().nullable(),
  needId: z.string().nullable(),
  travelerEmail: z.string(),
  travelerName: z.string().nullable(),
  /** `null` = pedido de planilha (ida, ou ida e volta). */
  direction: z.enum(['OUTWARD', 'RETURN']).nullable(),
  kind: MiceOrderEventKindSchema,
  /**
   * Código do voucher (`issued`), mensagem do erro (`failed`/`indeterminate`/`profile_write_failed`),
   * os dois preços (`price_drift`) ou os dois e-mails (`email_corrected`).
   */
  detail: z.string().nullable(),
  /** Operador da ação; `null` = worker. */
  by: z.string().nullable(),
  /** Nome do operador guardado na hora (eventos do cadastro). */
  byName: z.string().nullable().default(null),
  /** Campo do cadastro decidido (`profile_written`/`profile_kept`). */
  field: z.enum(MICE_PROFILE_FIELDS).nullable().default(null),
  /**
   * Antes e depois, como a view mostra (nome e nascimento em claro, CPF mascarado). Gravou: o da
   * VOLL → o gravado; manteve: o descartado da planilha → o da VOLL.
   */
  before: z.string().nullable().default(null),
  after: z.string().nullable().default(null),
  /** De onde veio o valor gravado. */
  source: z.enum(['sheet', 'typed']).nullable().default(null),
  at: z.iso.datetime(),
});
export type MiceOrderEventView = z.infer<typeof MiceOrderEventViewSchema>;

export const MiceActivityQuerySchema = z.object({
  /** Cursor opaco da página anterior (`nextBefore`). */
  before: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /**
   * Só os eventos do viajante com este e-mail (o histórico do painel dele). A chave é `email` porque
   * o api-kit mascara o valor dela nos logs e nos atributos `url.*`.
   */
  email: z.string().trim().min(1).max(320).optional(),
  /** `profile`: só as decisões do cadastro (`MICE_PROFILE_EVENT_KINDS`). */
  kinds: z.literal('profile').optional(),
});
export type MiceActivityQuery = z.infer<typeof MiceActivityQuerySchema>;

const isKnownKind = (item: unknown): boolean =>
  MiceOrderEventKindSchema.safeParse((item as { kind?: unknown } | null)?.kind).success;

export const MiceActivityPageSchema = z.object({
  /** Um `kind` que esta versão não conhece (servidor mais novo) é pulado, não derruba a página. */
  items: z
    .array(z.unknown())
    .transform((items) => items.filter(isKnownKind))
    .pipe(z.array(MiceOrderEventViewSchema)),
  /** `null` = não há eventos mais antigos. */
  nextBefore: z.string().nullable(),
});
export type MiceActivityPage = z.infer<typeof MiceActivityPageSchema>;
